import { useState } from 'react';
import { motion } from 'framer-motion';
import { PageHero, SectionHeader } from '@/components/common/Sections';
import { CTABanner } from '@/components/common/Cards';
import { OFFICES, COMPANY_INFO, IMAGES } from '@/lib/constants';
import { MapPin, Phone, EnvelopeSimple, Clock, WhatsappLogo } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { submitContactForm } from '@/lib/api';
import { toast } from 'sonner';

export default function ContactPage() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContactForm(formData);
      toast.success('Message sent successfully! We will contact you soon.');
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
      setAgreedToTerms(false);
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const hqOffice = OFFICES.find(o => o.isHQ);

  return (
    <div className="page-transition">
      <section data-testid="contact-section" className="pt-28 md:pt-32 pb-16 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <SectionHeader overline="Get in Touch" title="Send Us a Message" centered={false} />
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#0A1F3D] mb-2">Full Name *</label>
                    <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} data-testid="contact-name-input" required placeholder="Your name" className="rounded-none border-slate-300 focus:ring-[#D4AF37]" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A1F3D] mb-2">Email *</label>
                    <Input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} data-testid="contact-email-input" required placeholder="your@email.com" className="rounded-none border-slate-300 focus:ring-[#D4AF37]" />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-[#0A1F3D] mb-2">Phone *</label>
                    <Input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} data-testid="contact-phone-input" required placeholder="+91 XXXXX XXXXX" className="rounded-none border-slate-300 focus:ring-[#D4AF37]" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#0A1F3D] mb-2">Subject *</label>
                    <Select value={formData.subject} onValueChange={(value) => setFormData({...formData, subject: value})}>
                      <SelectTrigger data-testid="contact-subject-select" className="rounded-none border-slate-300"><SelectValue placeholder="Select subject" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="visa">Visa Services</SelectItem>
                        <SelectItem value="flights">Air Tickets</SelectItem>
                        <SelectItem value="packages">Tour Packages</SelectItem>
                        <SelectItem value="corporate">Corporate Travel</SelectItem>
                        <SelectItem value="study">Study Abroad</SelectItem>
                        <SelectItem value="forex">Forex Services</SelectItem>
                        <SelectItem value="other">Other Enquiry</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#0A1F3D] mb-2">Message *</label>
                  <Textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} data-testid="contact-message-input" required rows={5} placeholder="How can we help you?" className="rounded-none border-slate-300 focus:ring-[#D4AF37]" />
                </div>
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="terms-checkbox"
                    data-testid="contact-terms-checkbox"
                    checked={agreedToTerms}
                    onChange={(e) => setAgreedToTerms(e.target.checked)}
                    className="mt-1 w-4 h-4 accent-[#D4AF37] cursor-pointer flex-shrink-0"
                  />
                  <label htmlFor="terms-checkbox" className="text-sm text-[#4A5568] leading-relaxed cursor-pointer">
                    By clicking submit, I agree to the{' '}
                    <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] font-medium hover:underline">
                      Terms &amp; Conditions
                    </a>{' '}
                    and the{' '}
                    <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] font-medium hover:underline">
                      Privacy Policy
                    </a>{' '}
                    and give my consent to receive updates through SMS/Email.
                  </label>
                </div>
                <Button type="submit" data-testid="contact-submit-btn" disabled={loading || !agreedToTerms} className="w-full bg-[#D4AF37] text-[#0A1F3D] hover:bg-[#c5a033] py-6 rounded-none font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                  {loading ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2">
              <div className="bg-[#0A1F3D] text-white p-8 h-full">
                <h3 className="text-2xl font-bold font-['Playfair_Display'] mb-8">Contact Information</h3>
                {hqOffice && (
                  <div className="space-y-6">
                    <div className="flex items-start gap-4">
                      <MapPin size={24} className="text-[#D4AF37] flex-shrink-0 mt-1" />
                      <div>
                        <p className="font-medium mb-1">Corporate Headquarters</p>
                        <p className="text-white/70 text-sm">{hqOffice.address}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <Phone size={24} className="text-[#D4AF37] flex-shrink-0" />
                      <div>
                        <p className="font-medium mb-1">Phone</p>
                        <a href={`tel:${hqOffice.phone}`} className="text-white/70 text-sm hover:text-[#D4AF37]">{hqOffice.phone}</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <WhatsappLogo size={24} className="text-[#D4AF37] flex-shrink-0" />
                      <div>
                        <p className="font-medium mb-1">WhatsApp</p>
                        <a href={`https://wa.me/${COMPANY_INFO.whatsapp.replace(/[\s+-]/g, '')}`} className="text-white/70 text-sm hover:text-[#D4AF37]">{COMPANY_INFO.whatsapp}</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <EnvelopeSimple size={24} className="text-[#D4AF37] flex-shrink-0" />
                      <div>
                        <p className="font-medium mb-1">Email</p>
                        <a href={`mailto:${hqOffice.email}`} className="text-white/70 text-sm hover:text-[#D4AF37]">{hqOffice.email}</a>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <Clock size={24} className="text-[#D4AF37] flex-shrink-0" />
                      <div>
                        <p className="font-medium mb-1">Working Hours</p>
                        <p className="text-white/70 text-sm">{COMPANY_INFO.workingHours}</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}
